import { useState, useEffect } from "react";
import "../css/ServiceDirectory.css";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import MarkerClusterGroup from "@changey/react-leaflet-markercluster";
import { Icon, divIcon } from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIconPng from "../images/map_marker_2.svg";
import searchedMarkerPng from "../images/map_marker_1.svg";
import SearchAddressComponent from "../components/SearchAddressComponent.jsx";
import {
	Typography,
	List,
	ListItem,
	ListItemText,
	Divider,
	Switch,
} from "@mui/material";
import axios from "axios";
import Filters from "../components/Filters.jsx";

function ServiceDirectory() {
	const [selectedLocation, setSelectedLocation] = useState();
	const [selectedServiceCenterID, setSelectedServiceCenterID] = useState();
	const [servicesData, setServicesData] = useState();
	const [servicesDataFiltered, setServicesDataFiltered] = useState();
	const [listView, setListView] = useState(false);
	const [filters, setFilters] = useState();
	const [filtersSelected, setFiltersSelected] = useState();
	const [isCheckAllFilters, setIsCheckAllFilters] = useState();
	const [searchedMarker, setSearchedMarker] = useState();
	const [sortedServices, setSortedServices] = useState();
	useEffect(() => {
		if (selectedLocation !== undefined && selectedLocation) {
			setSearchedMarker({
				lat: selectedLocation.lat,
				lon: selectedLocation.lon,
				label: selectedLocation.display_name,
			});
		} else {
			setSearchedMarker(null);
		}
	}, [selectedLocation]);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		axios({
			action: "GET",
			url: "/service-directory/data",
		}).then((response) => {
			let servicesDataCopy = []
			let data =  response.data
			data.forEach((row, index) => {
				/**
				 * To address the issue where the "Service/Initiative name" is currently used as a key for "selectedServiceCenter," state
				 * we need to introduce an ID. The problem arises because "Service/Initiative name" is not unique; there can be multiple 
				 * services with the same name. Consequently, the "List of Services" section displays multiple services with the same name 
				 * selected simultaneously (even though each service has different addresses).
				 *
				 * Unfortunately, the "homlessLink" data does not provide any unique property. However, we can add an ID here, where each 
				 * row corresponds to its index in the "homlessLink" array.
				 */
				row["ID"]= index+1; 
				[	
					"services_provided",
					"services_which_address_social_isolation",
					"number_of_spaces",
				].forEach((column) => {
					if (typeof row[column] === "string") {
						row[column] = row[column].replaceAll("\n", "<br/>");
						row[column] = row[column].replaceAll("•\t", "-	");
						row[column] = row[column].replaceAll("•", "-");
					}
				});
				servicesDataCopy.push(row);
			});

			let filtersTemp = {
				"Service Type": [
					...new Set(
						servicesDataCopy
							.map((v) => v["type_of_service"])
							.reduce((acc, curr) => acc.concat(curr), [])
					),
				].sort(),
				"Boroughs Served": [
					...new Set(
						servicesDataCopy
							.map((v) => v["boroughs_served"])
							.reduce((acc, curr) => acc.concat(curr), [])
							.map((element) => element.trim())
					),
				].sort(),
			};

			const indexOther = filtersTemp["Service Type"].indexOf("Other");
			if (indexOther >= 0) {
				filtersTemp["Service Type"].splice(indexOther, 1);
				filtersTemp["Service Type"].push("Other");
			}
			setFilters(filtersTemp);
			setServicesData(servicesDataCopy);
			setServicesDataFiltered(servicesDataCopy);
		});
	}, []);

	useEffect(() => {
		if (filters) {
			setFiltersSelected(filters);
			const initialCheckAllState = {};
			Object.keys(filters).forEach((filterName) => {
				initialCheckAllState[filterName] = true;
			});
			setIsCheckAllFilters(initialCheckAllState);
		}
	}, [filters]);

	const updateServices = () => {
		const filteredData = servicesData.filter((service) => {
			return (
				filtersSelected["Service Type"].filter((serviceType) =>
					service["type_of_service"].includes(serviceType)
				).length > 0 &&
				(filtersSelected["Boroughs Served"].filter((boroughServed) =>
					service["boroughs_served"].includes(boroughServed)
				).length > 0 ||
					service["boroughs_served"] === "-")
			);
		});
		setServicesDataFiltered(filteredData);
	};

	const londonBounds = [
		[51.2867602, -0.5103759], // Southwest corner (bottom-left)
		[51.6918741, 0.3340155], // Northeast corner (top-right)
	];

	function fillNull(value){
		return value?value:'-'
	}

	useEffect(() => {
		if (
			selectedLocation !== undefined &&
			servicesDataFiltered !== undefined
		) {
			let servicesDataFilteredTemp =
				structuredClone(servicesDataFiltered);
			for (let i = 0; i < servicesDataFilteredTemp.length; i++) {
				servicesDataFilteredTemp[i]["distance"] = calculateDistance(
					servicesDataFilteredTemp[i]["coordinates"],
					[
						parseFloat(selectedLocation["lat"]),
						parseFloat(selectedLocation["lon"]),
					]
				);
			}
			servicesDataFilteredTemp.sort((a, b) => {
				if (a["distance"] === null && b["distance"] === null) {
					return 0;
				}
				if (a["distance"] === null) {
					return 1;
				}
				if (b["distance"] === null) {
					return -1;
				}
				return a["distance"] - b["distance"];
			});

			setSortedServices(servicesDataFilteredTemp);
		} else if (servicesDataFiltered !== undefined) {
			setSortedServices(servicesDataFiltered);
		}
	}, [selectedLocation, servicesDataFiltered]);

	function calculateDistance(coordinatesService, coordinatesSelection) {
		if (coordinatesService === null) {
			return null;
		}

		// Radius of the Earth in kilometers
		const r = 6371;

		// Convert latitude and longitude from degrees to radians
		const latService = (Math.PI / 180) * coordinatesService[0];
		const latSelection = (Math.PI / 180) * coordinatesSelection[0];
		const lonService = (Math.PI / 180) * coordinatesService[1];
		const lonSelection = (Math.PI / 180) * coordinatesSelection[1];

		// Haversine formula
		const dlat = latSelection - latService;
		const dlon = lonSelection - lonService;
		const a =
			Math.sin(dlat / 2) ** 2 +
			Math.cos(latService) *
				Math.cos(latSelection) *
				Math.sin(dlon / 2) ** 2;
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

		// Calculate the distance
		const calculatedDistance = r * c;
		return calculatedDistance;
	}
	// We can create our custom cluster icons using that function.
	// We can get the length of the cluster by cluster.getChildCount() and have different style
	// depending on the cluster length
	const createClusterCustomIcon = (cluster) => {
		return divIcon({
			html: `<div><span>${cluster.getChildCount()}</span></div>`,
			className: "custom-cluster-icon", // Define a CSS class for styling the cluster icon.
			iconSize: [40, 40], // Define the size of the cluster icon.
		});
	};
	return (
		<div id="service-directory-main">
			<div className="introduction-text-container">
				<div className="introduction-text-title">Service Directory</div>
				<div className="introduction-text-description">
					<Typography
						variant="body1"
						sx={{ fontSize: 18, fontWeight: "bold" }}
					>
						This map brings together services in London which offer
						activities or support which may help to address social
						isolation among individuals who have substance use
						issues and are rough sleeping or at risk of rough
						sleeping. Information has been drawn from the Homeless
						England database and additional research.
						<br /> <br />
						Users can navigate the service directory in two ways:
						<ol>
							<li>By using the map view (default option) to explore the different services across London.</li>
							<li>By shifting the toggle to the right to look at a list of services across London.</li>
						</ol>
						For both, filters allow users to filter either by service 
						type (e.g. Accommodation, Advice Centre etc.) or by boroughs 
						served (e.g. users can filter for services that work within 
						the borough of Camden). A specific service might work in 
						more than one borough, or it might be located in one borough 
						but work across a number of others.
						<br /><br />
						Users can also search for a specific address on the map, and 
						by shifting the toggle to the right they can see the list of 
						services sorted by proximity to the searched address.
						<br /> <br />
						Aware of services that are missing? Let us know at{" "}
						<span
							style={{
								textDecoration: "underline",
								color: "#651e7a",
							}}
						>
							<a href="mailto:servicemap@almaeconomics.com">
								servicemap@almaeconomics.com
							</a>
						</span>
					</Typography>
				</div>
			</div>
			<div id="services-section">
				<div id="services-container">
					<div id="service-filters-container">
						{filters && filtersSelected && (
							<Filters
								filters={filters}
								filtersSelected={filtersSelected}
								isCheckAllFilters={isCheckAllFilters}
								setFiltersSelected={setFiltersSelected}
								setIsCheckAllFilters={setIsCheckAllFilters}
								updateServices={updateServices}
								showClearAllButton={true}
								setServicesDataFiltered={
									setServicesDataFiltered
								}
								servicesData={servicesData}
							/>
						)}
					</div>
					<div id="map-actions-container">
						<div id="location-search-container">
							<SearchAddressComponent
								setSelectedLocation={setSelectedLocation}
								londonBounds={londonBounds}
							/>
						</div>
						<div
							id="page-view-switch-container"
							onClick={() => setListView(!listView)}
						>
							<Typography
								variant="body1"
								style={{
									color: !listView ? "#651e7a" : "inherit",
									fontWeight: "bold",
									cursor: "pointer",
								}}
								onChange={() => setListView(false)}
							>
								Map View
							</Typography>
							<Switch
								className="themed-switch"
								checked={listView}
								onChange={() => setListView(!listView)}
								inputProps={{
									"aria-label": "controlled",
								}}
								disableRipple
							/>
							<Typography
								variant="body1"
								style={{
									color: listView ? "#651e7a" : "inherit",
									fontWeight: "bold",
									cursor: "pointer",
								}}
								onChange={() => setListView(true)}
							>
								List of Services
							</Typography>
						</div>
					</div>

					{!listView ? (
						<MapContainer
							bounds={londonBounds}
							minZoom={10}
							maxBounds={londonBounds}
							id="services-map-container"
						>
							<TileLayer
								attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
								url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
							/>
							<MarkerClusterGroup
								showCoverageOnHover={false}
								singleMarkerMode={true}
								iconCreateFunction={createClusterCustomIcon}
							>
								{servicesDataFiltered &&
									servicesDataFiltered.map(
										(serviceData, index) =>
											serviceData["coordinates"] && (
												<Marker
													position={
														serviceData[
															"coordinates"
														]
													}
													icon={
														new Icon({
															iconUrl:
																markerIconPng,
															iconSize: [25, 25],
														})
													}
													key={"marker-" + index}
													eventHandlers={{
														click: () =>
															setSelectedServiceCenterID(
																serviceData[
																	"ID"
																]
															),
													}}
												>
													<Popup>
														{
															serviceData[
																"service_name"
															]
														}
														<br />
														{serviceData["address"] ? serviceData["address"] : "-"}
													</Popup>
												</Marker>
											)
									)}
							</MarkerClusterGroup>
							{searchedMarker && searchedMarker !== undefined && (
								<>
									<Marker
										position={[
											searchedMarker.lat,
											searchedMarker.lon,
										]}
										icon={
											new Icon({
												iconUrl: searchedMarkerPng,
												iconSize: [25, 25],
											})
										}
									>
										<Popup>{searchedMarker.label}</Popup>
									</Marker>
								</>
							)}
						</MapContainer>
					) : (
						<div id="services-list-container">
							{sortedServices.length ? (
								sortedServices.map((row) => (
									<>
										<List
											dense={true}
											key={
												"list-" +
												row["ID"]
											}
										>
											<ListItem
												className={
													row[
														"ID"
													] === selectedServiceCenterID
														? "selected-service-container"
														: ""
												}
												onClick={() => {
													setSelectedServiceCenterID(
														row["ID"]
													);
												}}
											>
												{/* <ListItemIcon size="small">
														<HomeWorkOutlined
															style={{
																color: "#A569B6",
															}}
														/>
													</ListItemIcon> */}
												<ListItemText
													primary={
														row[
															"service_name"
														]
													}
												/>
											</ListItem>
										</List>

										<Divider variant="middle" />
									</>
								))
							) : (
								<Typography
									variant="body1"
									style={{ marginTop: 20 }}
								>
									No available services
								</Typography>
							)}
						</div>
					)}
				</div>

				<div id="service-information-container">
					<div id="service-information-header">
						<Typography variant="h5" sx={{ fontWeight: "bold" }}>
							Service Information
						</Typography>
					</div>
					{selectedServiceCenterID ? (
						<div id="service-information-content">
							{typeof servicesDataFiltered.filter(
								(row) =>
									row["ID"] ===
									selectedServiceCenterID
							)[0] !== "undefined" ? (
								<>
									<p>
										<Typography
											variant="body1"
											style={{
												fontWeight: "bold",
												color: "#651e7a",
											}}
										>
											{servicesDataFiltered
												.filter(
													(row) =>
														row[
															"ID"
														] ===
														selectedServiceCenterID
												)
												.map((row) => (
													<>{row["service_name"]}</>
												))}
										</Typography>
									</p>
									<p>
										<Typography
											variant="body1"
											style={{
												fontWeight: "bold",
											}}
										>
											Address
										</Typography>
										<Typography variant="body2">
											{
												<div
													dangerouslySetInnerHTML={{
														__html: fillNull(servicesDataFiltered.filter(
															(row) =>
																row[
																	"ID"
																] ===
																selectedServiceCenterID
														)[0]["address"]),
													}}
												/>
											}
										</Typography>
									</p>
									<p>
										<Typography
											variant="body1"
											style={{
												fontWeight: "bold",
											}}
										>
											Website
										</Typography>
										<Typography variant="body2">
											{servicesDataFiltered
												.filter(
													(row) =>
														row[
															"ID"
														] ===
														selectedServiceCenterID
												)
												.map((row) => (
													<a
														key={row.id}
														href={row["website"]}
														target="_blank"
														rel="noopener noreferrer"
													>
														{fillNull(row["website"])}
													</a>
												))}
										</Typography>
									</p>

									<p>
										<Typography
											variant="body1"
											style={{
												fontWeight: "bold",
											}}
										>
											Services provided
										</Typography>
										<Typography variant="body2">
											{
												<div
													dangerouslySetInnerHTML={{
														__html: fillNull(servicesDataFiltered.filter(
															(row) =>
																row[
																	"ID"
																] ===
																selectedServiceCenterID
														)[0][
															"services_provided"
														]),
													}}
												/>
											}
										</Typography>
									</p>
									<p>
										<Typography
											variant="body1"
											style={{
												fontWeight: "bold",
											}}
										>
											Services which address social
											isolation
										</Typography>
										<Typography variant="body2">
											{
												<div
													dangerouslySetInnerHTML={{
														__html: fillNull(servicesDataFiltered.filter(
															(row) =>
																row[
																	"ID"
																] ===
																selectedServiceCenterID
														)[0][
															"services_which_address_social_isolation"
														]),
													}}
												/>
											}
										</Typography>
									</p>
									<p>
										<Typography
											variant="body1"
											style={{
												fontWeight: "bold",
											}}
										>
											Boroughs served
										</Typography>
										<Typography variant="body2">
											{	
												<div
													dangerouslySetInnerHTML={{
														__html: fillNull(servicesDataFiltered.filter(
															(row) =>
																row[
																	"ID"
																] ===
																selectedServiceCenterID
														)[0][
															"boroughs_served"
														]).join(" / "),
													}}
												/>
											}
										</Typography>
									</p>
									<p>
										<Typography
											variant="body1"
											style={{
												fontWeight: "bold",
											}}
										>
											Number of spaces
										</Typography>
										<Typography variant="body2">
											{
												<div
													dangerouslySetInnerHTML={{
														__html: fillNull(servicesDataFiltered.filter(
															(row) =>
																row[
																	"ID"
																] ===
																selectedServiceCenterID
														)[0][
															"number_of_spaces"
														]),
													}}
												/>
											}
										</Typography>
									</p>

									<p>
										<Typography
											variant="body1"
											style={{
												fontWeight: "bold",
											}}
										>
											Length of stay
										</Typography>
										<Typography variant="body2">
											{
												fillNull(servicesDataFiltered.filter(
													(row) =>
														row[
															"ID"
														] ===
														selectedServiceCenterID
												)[0]["lenght_of_stay"])
											}
										</Typography>
									</p>
									<p>
										<Typography
											variant="body1"
											style={{
												fontWeight: "bold",
											}}
										>
											Target age cohort
										</Typography>
										<Typography variant="body2">
											{" "}
											{
												fillNull(servicesDataFiltered.filter(
													(row) =>
														row[
															"ID"
														] ===
														selectedServiceCenterID
												)[0]["target_age_cohort"])
											}
										</Typography>
									</p>
									<p>
										<Typography
											variant="body1"
											style={{
												fontWeight: "bold",
											}}
										>
											Target population by gender
										</Typography>
										<Typography variant="body2">
											{" "}
											{
												fillNull(servicesDataFiltered.filter(
													(row) =>
														row[
															"ID"
														] ===
														selectedServiceCenterID
												)[0][
													"target_population_by_gender"
												])
											}
										</Typography>
									</p>
									{/* <p>
												<Typography
													variant="body1"
													style={{
														fontWeight: "bold",
													}}
												>
													Arson/sex offending
													exclusion
												</Typography>
												<Typography variant="body2">
													{
														servicesData.filter(
															(row) =>
																row[
																	"service_name"
																] ===
																selectedServiceCenter
														)[0][
															"Arson/sex offending exclusion"
														]
													}
												</Typography>
											</p> */}
									<p>
										<Typography
											variant="body1"
											style={{
												fontWeight: "bold",
											}}
										>
											Referral process
										</Typography>
										<Typography variant="body2">
											{
												fillNull(servicesDataFiltered.filter(
													(row) =>
														row[
															"ID"
														] ===
														selectedServiceCenterID
												)[0]["referral_process"])
											}
										</Typography>
									</p>
								</>
							) : (
								<div id="data-dashboard-notification-text">
									<Typography variant="body1">
										No data available.
										<br />
										Select a service to view the available
										data.
									</Typography>
								</div>
							)}
						</div>
					) : (
						<div id="data-dashboard-notification-text">
							<Typography variant="body1">
								No data available.
								<br />
								Select a service to view the available data.
							</Typography>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export default ServiceDirectory;